import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import "../AddWorkReport/work_report.css";
import DataTable from "../../components/custom_data_table";
import {
  addAdditionalWorkReport,
  getAdditionalWorkReports,
  getAllAssignedProjects,
} from "../../service/project_service";
import { AlertCircle } from "lucide-react";
import CustomLoadingindicator from "../../components/custom_loading";
import { CustomSearchableSelect } from "../../components/admin/AdminFields";

const AdditionalWorkReport = () => {
  const [formData, setFormData] = useState({
    projectid: "",
    workdate: new Date().toISOString().split("T")[0],
    starttime: "",
    endtime: "",
    totalhours: "",
    description: "",
  });
  const [projects, setProjects] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isTimeRestricted, setIsTimeRestricted] = useState(false);

  const checkTimeRestriction = useCallback(() => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const currentTotalMinutes = hours * 60 + minutes;

    // 6:30 PM (18:30) = 1110 minutes
    // 9:30 AM (09:30) = 570 minutes
    // The window is open between 18:30 (current day) and 09:30 (next day)
    const startWindow = 1110;
    const endWindow = 570;

    // Use OR since it's an overnight window
    const isInsideWindow =
      currentTotalMinutes >= startWindow || currentTotalMinutes <= endWindow;

    setIsTimeRestricted(!isInsideWindow);
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await getAllAssignedProjects();
      if (response?.status === "success") {
        const uniqueProjects = Array.from(
          new Map((response.data || []).map((p) => [p.projectid, p])).values(),
        );
        setProjects(uniqueProjects);
      }
    } catch (error) {
      toast.error(error?.message || "Failed to fetch projects");
    }
  }, []);



  const formatTime12h = (timeStr) => {
    if (!timeStr || timeStr === "-") return "-";
    try {
      const [h, m] = timeStr.split(":").map(Number);
      const period = h >= 12 ? "PM" : "AM";
      const hours12 = h % 12 || 12;
      return `${hours12.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")} ${period}`;
    } catch (e) {
      return timeStr;
    }
  };

  const formatDuration = (totalHoursStr) => {
    if (!totalHoursStr || totalHoursStr === "0") return "-";
    try {
      const [h, m] = totalHoursStr.split(".").map(Number);
      const hPart = h > 0 ? `${h}hr ` : "";
      const mPart = (m || 0) > 0 ? `${m} mins` : "";
      return (hPart + mPart).trim() || "0 mins";
    } catch (e) {
      return totalHoursStr;
    }
  };

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAdditionalWorkReports();
      if (response?.status === "success") {
        const formatted = (response.data || []).map((report, index) => ({
          ...report,
          sno: index + 1,
          projectName: report.projectname,
          workedDate: report.workdate,
          starttime: formatTime12h(report.starttime),
          endtime: formatTime12h(report.endtime),
          totalhours: formatDuration(report.totalhours),
        }));
        setReports(formatted.reverse());
      }
    } catch (error) {
      toast.error(error?.message || "Failed to fetch reports");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
    fetchReports();
    checkTimeRestriction();
    const timer = setInterval(checkTimeRestriction, 60000);
    return () => clearInterval(timer);
  }, [fetchProjects, fetchReports, checkTimeRestriction]);


  // Calculate duration
  useEffect(() => {
    if (formData.starttime && formData.endtime) {
      const [startH, startM] = formData.starttime.split(":").map(Number);
      const [endH, endM] = formData.endtime.split(":").map(Number);

      const startTotalMins = startH * 60 + startM;
      const endTotalMins = endH * 60 + endM;

      if (endTotalMins > startTotalMins) {
        const diffMins = endTotalMins - startTotalMins;
        const h = Math.floor(diffMins / 60);
        const m = diffMins % 60;
        const formatted = `${h}.${m.toString().padStart(2, "0")}`;
        setFormData((prev) => ({
          ...prev,
          totalhours: formatted,
        }));
      } else {
        setFormData((prev) => ({ ...prev, totalhours: "" }));
      }
    }
  }, [formData.starttime, formData.endtime]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Strict time validation for starttime and endtime
    if (name === "starttime" || name === "endtime") {
      const [h, m] = value.split(":").map(Number);
      const totalMinutes = h * 60 + m;

      // Forbidden range: 9:30 AM (570) to 6:30 PM (1110)
      if (totalMinutes > 570 && totalMinutes < 1110) {
        toast.error("Daytime entries (9:31 AM - 6:29 PM) are restricted for additional work reports.");
        return; // Don't update state
      }
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    setFormData({
      projectid: "",
      workdate: new Date().toISOString().split("T")[0],
      starttime: "",
      endtime: "",
      totalhours: "",
      description: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isTimeRestricted) {
      toast.error(
        "Submission is only allowed between 6:30 PM and next day 9:30 AM",
      );
      return;
    }

    const validateTimeSlot = (timeStr) => {
      if (!timeStr) return false;
      const [h, m] = timeStr.split(":").map(Number);
      const total = h * 60 + m;
      return total >= 1110 || total <= 570; // 18:30 to 09:30
    };

    if (
      !validateTimeSlot(formData.starttime) ||
      !validateTimeSlot(formData.endtime)
    ) {
      toast.error(
        "Selected times must be between 6:30 PM and 9:30 AM (next day)",
      );
      return;
    }

    const [startH, startM] = formData.starttime.split(":").map(Number);
    const [endH, endM] = formData.endtime.split(":").map(Number);
    const startTotalMins = startH * 60 + startM;
    const endTotalMins = endH * 60 + endM;

    if (startTotalMins >= endTotalMins) {
      toast.error("End Time must be greater than Start Time");
      return;
    }

    if (
      !formData.projectid ||
      !formData.workdate ||
      !formData.starttime ||
      !formData.endtime ||
      !formData.totalhours ||
      !formData.description.trim()
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    setSubmitLoading(true);
    try {
      const response = await addAdditionalWorkReport(formData);
      if (response?.status === "success") {
        toast.success(
          response?.message || "Additional work report added successfully",
        );
        handleReset();
        fetchReports();
      }
    } catch (err) {
      toast.error(err?.message || "Failed to add report");
    } finally {
      setSubmitLoading(false);
    }
  };

  const columns = [
    { key: "sno", label: "S.No" },
    { key: "projectName", label: "Project Name" },
    { key: "workedDate", label: "Work Date" },
    { key: "starttime", label: "Start Time" },
    { key: "endtime", label: "End Time" },
    { key: "totalhours", label: "Total Hours" },
    { key: "description", label: "Description" },
  ];

  if (loading && reports.length === 0) {
    return <CustomLoadingindicator />;
  }

  return (
    <div className="add-report-container">
      <h4>Additional Work Report</h4>

      {isTimeRestricted && (
        <div
          style={{
            background: "#fff1f2",
            border: "1px solid #fecaca",
            padding: "16px",
            borderRadius: "12px",
            color: "#b91c1c",
            marginBottom: "20px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            fontSize: "14px",
            fontWeight: "600"
          }}
        >
          <AlertCircle size={20} />
          Submission restricted. You can only add reports between 6:30 PM and
          next day morning 9:30 AM.
        </div>
      )}

      <div className={`add-report-card ${isTimeRestricted ? 'restricted-view' : ''}`}
        style={{ opacity: isTimeRestricted ? 0.7 : 1, pointerEvents: isTimeRestricted ? 'none' : 'auto' }}>
        <form className="add-report-form" onSubmit={handleSubmit}>
          <div className="report-form-group">
            <CustomSearchableSelect
              label="Project *"
              placeholder="Select Project"
              value={formData.projectid}
              onChange={(e) =>
                handleChange({
                  target: { name: "projectid", value: e.target.value },
                })
              }
              options={projects.map((item) => ({
                label: item.projectname,
                value: item.projectid,
              }))}
            />
          </div>

          <div className="report-form-group">
            <label>Work Date *</label>
            <input
              type="date"
              name="workdate"
              value={formData.workdate}
              onChange={handleChange}
              required
            />
          </div>
          <div className="report-form-group">
            <label>Start Time *</label>
            <input
              type="time"
              name="starttime"
              value={formData.starttime}
              onChange={handleChange}
              required
            />
          </div>

          <div className="report-form-group">
            <label>End Time *</label>
            <input
              type="time"
              name="endtime"
              value={formData.endtime}
              onChange={handleChange}
              required
            />
          </div>

          <div className="report-form-group">
            <label>Total Hours *</label>
            <input
              type="text"
              name="totalhours"
              value={formData.totalhours}
              disabled
              placeholder="Calculated automatically"
              required
            />
          </div>

          <div className="report-form-group report-form-group-full">
            <label>Description *</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              placeholder="Describe your additional work..."
              required
            />
          </div>

          <div className="submit-buttons report-form-group-full">
            <button
              type="submit"
              className="apply-btn"
              disabled={submitLoading || isTimeRestricted}
            >
              {submitLoading ? "Submitting..." : "Submit Report"}
            </button>
            <button
              type="button"
              className="reset-btn"
              onClick={handleReset}
              disabled={submitLoading}
            >
              Reset
            </button>
          </div>
        </form>
      </div>

      <div style={{ height: "40px" }}></div>

      <DataTable
        columns={columns}
        data={reports}
        loading={loading}
        pageSize={10}
      />
      <div style={{ height: "50px" }}></div>
    </div>
  );
};

export default AdditionalWorkReport;
